import type { Article } from "../../../types/blog";

const article: Article = {
  slug: "mot-de-passe-windows-oublie",
  silo: "windows",
  h1: "Mot de passe Windows oublié : comment récupérer l'accès à votre PC ?",
  metaTitle: "Mot de passe Windows oublié : récupérer l'accès facilement",
  metaDescription: "Vous avez oublié votre mot de passe Windows et vous êtes bloqué ? Découvrez les solutions légales pour retrouver l'accès à votre ordinateur.",
  excerpt: "Vous avez oublié votre mot de passe Windows et votre PC vous refuse l'accès ? Selon le type de compte (local ou Microsoft), les solutions sont différentes. Voici les méthodes officielles pour retrouver l'accès.",
  publishedAt: "2024-01-25",
  readingMinutes: 5,
  relatedSlugs: ["ordinateur-ne-demarre-plus", "pc-bloque-demarrage-windows", "erreurs-windows-frequentes"],
  faq: [
    {
      question: "Quelle est la différence entre un compte local et un compte Microsoft ?",
      answer: "Un compte Microsoft est lié à votre adresse email (outlook.com, hotmail.com, etc.) et vous pouvez réinitialiser le mot de passe en ligne. Un compte local est propre à votre PC et la récupération est différente.",
    },
    {
      question: "Peut-on récupérer ses données si on ne connaît plus le mot de passe ?",
      answer: "Oui, dans la plupart des cas. Même sans connaître le mot de passe, un technicien peut accéder aux fichiers via un outil de démarrage externe, récupérer vos données et réinitialiser l'accès.",
    },
    {
      question: "Un technicien peut-il réinitialiser mon mot de passe à distance ?",
      answer: "Pour un compte Microsoft, oui -- la procédure se fait en ligne depuis n'importe quel appareil. Pour un compte local, une intervention via un support de démarrage peut être nécessaire.",
    },
  ],
  content: [
    {
      type: "p",
      text: "Oublier son mot de passe Windows est une situation bien plus courante qu'on ne le pense. Pas de panique -- des solutions officielles existent selon le type de compte Windows que vous utilisez.",
    },
    {
      type: "h2",
      text: "Êtes-vous connecté avec un compte Microsoft ou un compte local ?",
    },
    {
      type: "p",
      text: "C'est la première question à se poser. Si votre compte de connexion est une adresse email (ex : prénom@outlook.com), vous avez un compte Microsoft. Si c'est juste un nom d'utilisateur sans @, c'est un compte local. La procédure de récupération est différente dans chaque cas.",
    },
    {
      type: "h2",
      text: "Cas 1 : compte Microsoft (adresse email)",
    },
    {
      type: "p",
      text: "C'est le cas le plus simple. Depuis n'importe quel appareil (téléphone, tablette, autre PC), rendez-vous sur account.microsoft.com. Cliquez sur 'Mot de passe oublié' et suivez les instructions. Microsoft envoie un code de vérification par email ou SMS. Une fois le mot de passe réinitialisé, vous pouvez vous connecter sur votre PC.",
    },
    {
      type: "h2",
      text: "Cas 2 : compte local Windows 10/11",
    },
    {
      type: "p",
      text: "Si vous avez configuré des questions de sécurité lors de la création du compte, Windows vous les posera après 3 tentatives échouées. Répondez correctement et vous pouvez créer un nouveau mot de passe.",
    },
    {
      type: "callout",
      variant: "info",
      title: "Compte administrateur local caché",
      text: "Windows possède un compte administrateur intégré qui est désactivé par défaut. Un technicien peut l'activer depuis l'environnement de récupération pour réinitialiser votre mot de passe.",
    },
    {
      type: "h2",
      text: "Si vous n'avez ni email de récupération ni questions de sécurité",
    },
    {
      type: "p",
      text: "Dans ce cas, la récupération nécessite un support de démarrage externe (clé USB Windows). C'est une manipulation technique que nos techniciens maîtrisent parfaitement. Nous pouvons vous guider à distance pour :\n• Créer un outil de réinitialisation\n• Accéder à l'environnement de récupération\n• Réinitialiser le mot de passe sans perdre vos données",
    },
    {
      type: "h2",
      text: "Prévenir le problème à l'avenir",
    },
    {
      type: "ul",
      items: [
        "Utilisez un compte Microsoft plutôt qu'un compte local -- la récupération de mot de passe est beaucoup plus simple",
        "Configurez un code PIN en plus du mot de passe (Paramètres > Comptes > Options de connexion)",
        "Notez votre mot de passe dans un endroit sécurisé physique",
        "Configurez Windows Hello (reconnaissance faciale ou empreinte digitale) si votre PC le supporte",
      ],
    },
    {
      type: "h2",
      text: "Intervention à distance pour récupérer l'accès",
    },
    {
      type: "p",
      text: "Nos techniciens peuvent vous guider étape par étape pour récupérer l'accès à votre PC, même à distance. Si votre PC était également bloqué au démarrage, consultez notre guide sur l'[ordinateur qui ne démarre plus](slug:ordinateur-ne-demarre-plus).",
    },
  ],
};

export default article;
